import { useState, useEffect } from "react";
import { toDateLocal } from "../../utils/dateUtils";
import { classifySleepType } from "../../utils/wakeWindowUtils";

/**
 * SleepForm — add a sleep entry.
 *
 * Props:
 *   onAdd          — called with the new entry object
 *   initialValues  — optional pre-fill from a stopped timer:
 *                    { date, startTime, endTime }
 *
 * The "Type" field is auto-classified from the start/end times. If the user
 * manually changes the select it is respected, but it resets to the derived
 * value whenever startTime or endTime changes.
 */
export default function SleepForm({ onAdd, initialValues }) {
  const [date, setDate]           = useState(() => initialValues?.date      || toDateLocal());
  const [startTime, setStartTime] = useState(() => initialValues?.startTime || "");
  const [endTime, setEndTime]     = useState(() => initialValues?.endTime   || "");
  const [notes, setNotes]         = useState("");

  /** Auto-derive sleep type from current start / end times. */
  function deriveType(start, end) {
    if (!start) return 'nap';
    let durationMin = 0;
    if (end) {
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      const diff = (eh * 60 + em) - (sh * 60 + sm);
      durationMin = diff >= 0 ? diff : diff + 24 * 60; // overnight
    }
    return classifySleepType(start, durationMin);
  }

  const [type, setType] = useState(() => deriveType(initialValues?.startTime || '', initialValues?.endTime || ''));

  // Re-classify whenever the user changes start or end time
  useEffect(() => {
    setType(deriveType(startTime, endTime));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, endTime]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!startTime) return;

    onAdd({
      date: new Date(`${date}T${startTime}:00`).toISOString(),
      startTime,
      endTime: endTime || null,
      type,
      notes: notes || "",
    });

    setDate(toDateLocal());
    setStartTime("");
    setEndTime("");
    setType('nap');
    setNotes("");
  };

  return (
    <form onSubmit={handleSubmit} className="mb-3">
      <div className="row g-2">
        <div className="col-md-2">
          <label className="form-label">Date</label>
          <input
            type="date"
            className="form-control"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </div>
        <div className="col-md-2">
          <label className="form-label">Start Time</label>
          <input
            type="time"
            className="form-control"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            required
          />
        </div>
        <div className="col-md-2">
          <label className="form-label">End Time</label>
          <input
            type="time"
            className="form-control"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="nap">Nap</option>
            <option value="night">Night Sleep</option>
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">Notes</label>
          <input
            type="text"
            className="form-control"
            placeholder="Optional"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <button type="submit" className="btn btn-primary w-100">
            Add Sleep
          </button>
        </div>
      </div>
    </form>
  );
}
